import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { MessageCircle, X, Send, Bot, User as UserIcon, Loader2, ShieldCheck } from "lucide-react";

const SESSION_KEY = "ds_ai_session";
const HISTORY_KEY = "ds_ai_history";

function newSessionId() {
  return `web-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

export default function PublicAIChatWidget() {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem(SESSION_KEY) || newSessionId();
  });
  const [messages, setMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [input, setInput] = useState("");
  const [bookingNumber, setBookingNumber] = useState("");
  const [contactKey, setContactKey] = useState("");
  const [sending, setSending] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(SESSION_KEY, sessionId);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
  }, [sessionId, messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages, sending]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        text: t("¡Hola! Soy SofIA, la asistente virtual de DS Travel Tours. ¿En qué puedo ayudarte?"),
        ts: Date.now(),
      }]);
    }
  }, [open, messages.length, t]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text, ts: Date.now() }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/public/ai-chat`, {
        session_id: sessionId,
        message: text,
        booking_number: bookingNumber || "",
        contact_key: contactKey || "",
        lang,
      });
      setMessages((m) => [...m, {
        role: "assistant",
        text: data.reply,
        escalated: data.escalated,
        verified: data.verified,
        ts: Date.now(),
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: formatApiErrorDetail(e?.response?.data?.detail) || e.message,
        error: true,
        ts: Date.now(),
      }]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    const nid = newSessionId();
    setSessionId(nid);
    setMessages([]);
    setBookingNumber("");
    setContactKey("");
    setShowVerify(false);
    localStorage.setItem(SESSION_KEY, nid);
    localStorage.setItem(HISTORY_KEY, "[]");
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-chat-fab"
          aria-label={t("Abrir chat de ayuda")}
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-black text-white shadow-2xl hover:bg-[#F5B301] hover:text-black transition-colors flex items-center justify-center border-2 border-black"
        >
          <MessageCircle size={22} />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 right-5 z-50 w-[calc(100vw-2.5rem)] sm:w-[520px] h-[720px] max-h-[calc(100vh-2.5rem)] bg-white border-2 border-black shadow-2xl flex flex-col"
          data-testid="ai-chat-panel"
        >
          <header className="bg-black text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#F5B301] text-black flex items-center justify-center">
                <Bot size={16} />
              </div>
              <div>
                <div className="text-sm font-display font-bold leading-none">SofIA</div>
                <div className="text-[9px] uppercase tracking-widest text-white/70 mt-1">
                  {t("Asistente DS Travel Tours")}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={resetChat}
                title={t("Nueva conversación")}
                className="text-[10px] uppercase tracking-widest text-white/70 hover:text-white"
                data-testid="ai-chat-reset"
              >
                {t("Nueva")}
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label={t("Cerrar chat")}
                data-testid="ai-chat-close"
                className="w-7 h-7 flex items-center justify-center hover:bg-white/10"
              >
                <X size={16} />
              </button>
            </div>
          </header>

          {/* Verification banner */}
          {!showVerify && !bookingNumber && (
            <button
              onClick={() => setShowVerify(true)}
              className="bg-zinc-50 border-b border-zinc-200 px-3 py-2 text-[11px] flex items-center gap-2 hover:bg-zinc-100 text-left"
              data-testid="ai-chat-open-verify"
            >
              <ShieldCheck size={13} className="text-zinc-500" />
              <span>
                <b>{t("¿Consulta sobre tu reserva?")}</b> {t("Verifícate primero →")}
              </span>
            </button>
          )}
          {showVerify && (
            <div className="bg-zinc-50 border-b border-zinc-200 p-3 space-y-2" data-testid="ai-chat-verify-form">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <ShieldCheck size={11} /> {t("Verificación (opcional)")}
              </div>
              <input
                value={bookingNumber}
                onChange={(e) => setBookingNumber(e.target.value.toUpperCase())}
                placeholder={t("Nº de reserva (DSG-XXXXX)")}
                className="w-full text-sm h-9 px-2 border border-black rounded-none"
                data-testid="ai-chat-booking-input"
              />
              <input
                value={contactKey}
                onChange={(e) => setContactKey(e.target.value)}
                placeholder={t("Email o teléfono")}
                className="w-full text-sm h-9 px-2 border border-black rounded-none"
                data-testid="ai-chat-contact-input"
              />
              <button
                onClick={() => setShowVerify(false)}
                className="text-[10px] uppercase tracking-widest text-zinc-600 hover:text-black"
              >
                {t("Cerrar")}
              </button>
            </div>
          )}

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-3 space-y-3 bg-white"
            data-testid="ai-chat-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}
              >
                {m.role === "assistant" && (
                  <div className="w-7 h-7 bg-black text-white flex items-center justify-center shrink-0">
                    <Bot size={13} />
                  </div>
                )}
                <div
                  className={`max-w-[92%] px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                    m.role === "user"
                      ? "bg-black text-white"
                      : m.error
                      ? "bg-red-50 border border-red-300 text-red-800"
                      : m.escalated
                      ? "bg-amber-50 border border-amber-400 text-amber-900"
                      : "bg-zinc-100 text-zinc-900"
                  }`}
                >
                  {m.text}
                  {m.escalated && (
                    <div className="text-[10px] uppercase tracking-widest mt-2 opacity-75">
                      {t("Escalado a supervisor")}
                    </div>
                  )}
                </div>
                {m.role === "user" && (
                  <div className="w-7 h-7 bg-[#F5B301] text-black flex items-center justify-center shrink-0">
                    <UserIcon size={13} />
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex gap-2">
                <div className="w-7 h-7 bg-black text-white flex items-center justify-center">
                  <Bot size={13} />
                </div>
                <div className="bg-zinc-100 px-3 py-2 flex items-center gap-2 text-sm">
                  <Loader2 className="animate-spin" size={13} /> {t("Escribiendo…")}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-black p-2 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder={t("Escribe tu mensaje…")}
              className="flex-1 h-10 px-3 text-sm border border-black rounded-none"
              disabled={sending}
              data-testid="ai-chat-input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="ai-chat-send"
              className="w-10 h-10 bg-black text-white flex items-center justify-center hover:bg-[#F5B301] hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
